import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config';
import {
  MapPin,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Check,
  X
} from 'lucide-react';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { formatCurrency, formatMileage } from '../Utils/formatters';
import ImageCarousel from '../Components/ImageCarousel';

const FEATURE_GROUPS = {
  'Güvenlik': ['ABS', 'ESP / VSA', 'BAS', 'Hava Yastığı (Sürücü)', 'Hava Yastığı (Yolcu)', 'İsofix', 'Merkezi Kilit', 'İmmobilizer', 'Şerit Takip Sistemi', 'Gece Görüş Sistemi', 'Kör Nokta Uyarı Sistemi', 'Yorgunluk Tespit Sistemi', 'Yokuş Kalkış Desteği', 'Çocuk Kilidi', 'AEB', 'Distronic'],
  'İç Donanım': ['Deri Koltuk', 'Kumaş Koltuk', 'Elektrikli Camlar', 'Klima', 'Otomatik Klima', 'Start / Stop', 'Yol Bilgisayarı', 'Ön Koltuk Kol Dayaması', 'Head-up Display', 'Isıtmalı Direksiyon', 'Koltuklar (Isıtmalı)', 'Koltuklar (Soğutmalı)', 'Koltuklar (Hafızalı)', 'Elektrikli Koltuklar', 'Hız Sabitleme Sistemi', 'Adaptive Cruise Control', 'Geri Görüş Kamerası', 'Ön Görüş Kamerası', 'Anahtarsız Giriş ve Çalıştırma', 'Hidrolik Direksiyon', 'Fonksiyonel Direksiyon', 'Otm. Kararan Dikiz Aynası', 'Üçüncü Sıra Koltuklar'],
  'Dış Donanım': ['Sunroof', 'Panoramik Cam Tavan', 'Hardtop', 'Far (Adaptif)', 'Park Sensörü (Ön)', 'Park Sensörü (Arka)', 'Park Asistanı', 'Aynalar (Isıtmalı)', 'Aynalar (Hafızalı)', 'Aynalar (Elektrikli)', 'Akıllı Bagaj Kapağı', 'Römork Çeki Demiri', 'Ayakla Açılan Bagaj Kapağı'],
  'Multimedya': ['Android Auto', 'Apple CarPlay', 'Bluetooth', 'USB / AUX', 'Navigasyon', 'CD / DVD', 'Dokunmatik Ekran', 'Kablosuz Şarj'],
};

const ALL_PARTS = [
  { key: 'hood', label: 'Kaput' },
  { key: 'roof', label: 'Tavan' },
  { key: 'trunk', label: 'Bagaj' },
  { key: 'front_bumper', label: 'Ön Tampon' },
  { key: 'rear_bumper', label: 'Arka Tampon' },
  { key: 'front_left_fender', label: 'Sol Ön Çamurluk' },
  { key: 'front_right_fender', label: 'Sağ Ön Çamurluk' },
  { key: 'rear_left_fender', label: 'Sol Arka Çamurluk' },
  { key: 'rear_right_fender', label: 'Sağ Arka Çamurluk' },
  { key: 'front_left_door', label: 'Sol Ön Kapı' },
  { key: 'front_right_door', label: 'Sağ Ön Kapı' },
  { key: 'rear_left_door', label: 'Sol Arka Kapı' },
  { key: 'rear_right_door', label: 'Sağ Arka Kapı' },
];

const COLOR_MAP = { original: '#e5e7eb', local_painted: '#f97316', painted: '#3b82f6', changed: '#ef4444' };

const cleanPhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

const VehicleDetail = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_URL}/vehicles/${id}`)
      .then(res => res.json())
      .then(data => {
        const dbFeatures = data.features || [];
        const mappedVehicle = {
          ...data,
          title: data.name,
          adNo: `ERN-${data.id + 1000}`,
          adDate: new Date(data.created_at).toLocaleDateString('tr-TR'),
          location: { city: 'Kahramanmaraş', district: 'Onikişubat' },
          price_daily: data.price_daily || null,
          price_weekly: data.price_weekly || null,
          price_monthly: data.price_monthly || null,
          specs: {
            fuelType: data.fuel_type || 'Belirtilmemiş',
            transmission: data.transmission || 'Belirtilmemiş',
            km: formatMileage(data.mileage),
            color: data.color || 'Belirtilmemiş',
            enginePower: data.engine_power || 'Belirtilmemiş',
            engineCapacity: data.engine_capacity || 'Belirtilmemiş',
            exchange: data.exchange || 'Belirtilmemiş',
            traction: data.traction || 'Belirtilmemiş',
            bodyType: data.body_type || 'Belirtilmemiş',
            fromWho: data.from_who || 'Galeriden',
            heavyDamage: data.heavy_damage || 'Yok'
          },
          damageStatus: { original: data.heavy_damage === 'Ağır Hasarlı' ? 'Ağır Hasarlı' : 'Hatasız / Boyasız' },
          eidsInfo: { dealerName: data.from_who === 'Sahibinden' ? 'Sahibinden' : 'Erenis Otomotiv', plate: data.plate_nationality || 'Belirtilmemiş' },
          expertise_report: data.expertise_report || null,
          damage_amount: data.damage_amount || 0,
          expertise_note: data.expertise_note || '',
          dbFeatures: dbFeatures,
          equipment: {
            security: FEATURE_GROUPS['Güvenlik'].filter(f => dbFeatures.includes(f)),
            interior: FEATURE_GROUPS['İç Donanım'].filter(f => dbFeatures.includes(f)),
            exterior: FEATURE_GROUPS['Dış Donanım'].filter(f => dbFeatures.includes(f)),
          },
          features: {
            rentalType: ['Günlük', 'Haftalık', 'Aylık'],
            paidOptions: ['Bebek Koltuğu', 'Ek Sürücü'],
            deliveryOptions: ['Havalimanı', 'Adrese Teslim']
          },
          seller: {
            name: data.seller_name || 'Oğuzhan Eren',
            phone: data.seller_phone || '+90 532 158 56 46'
          }
        };
        setVehicle(mappedVehicle);
        setLoading(false);
      })
      .catch(err => {
        console.error('Araç detayı yüklenemedi:', err);
        setLoading(false);
      });

    fetch(`${API_URL}/vehicles/${id}/availability`)
      .then(res => res.json())
      .then(data => setAvailability(data))
      .catch(err => console.error('Takvim bilgileri alınamadı:', err));
  }, [id]);

  if (loading) return <div className="min-h-screen py-32 text-center text-white bg-bg-main">Araç yükleniyor...</div>;
  if (!vehicle) return <div className="min-h-screen py-32 text-center text-white bg-bg-main">Araç bulunamadı.</div>;

  const isRental = vehicle.type === 'Kiralık';

  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const calendarDays = [];
    const startingDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    for (let i = 0; i < startingDay; i++) {
      calendarDays.push(<div key={`empty-${i}`} style={{ height: '110px', background: 'rgba(255, 255, 255, 0.01)', border: '1px solid rgba(255, 255, 255, 0.03)' }}></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = availability.filter(h => {
        const start = h.start_date.split('T')[0];
        const end = h.end_date.split('T')[0];
        return dateStr >= start && dateStr <= end;
      });

      const cellDate = new Date(year, month, day);
      cellDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isToday = cellDate.getTime() === today.getTime();

      const isOccupied = dayEvents.length > 0;

      const isPast = cellDate.getTime() < today.getTime();

      const getCellStyle = () => {
        if (isPast) return { color: 'var(--text-dim)', icon: 'past' };
        if (isOccupied) return {
          color: 'rgb(248, 113, 113)',
          icon: 'occupied'
        };
        return {
          color: 'rgb(74, 222, 128)',
          icon: 'available'
        };
      };

      const config = getCellStyle();

      const cellStyle = {
        height: '110px',
        background: 'rgba(255, 255, 255, 0.015)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        padding: '0.6rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: isPast ? 'default' : 'pointer',
        userSelect: 'none',
        zIndex: 1,
        overflow: 'hidden',
        backdropFilter: 'blur(4px)'
      };

      const dayNumberStyle = {
        fontSize: '0.85rem',
        fontWeight: isPast ? 500 : 800,
        color: config.color,
        opacity: isPast ? 0.4 : 1,
        letterSpacing: '-0.02em'
      };

      calendarDays.push(
        <div key={day} style={cellStyle} className={`calendar-cell-hover ${isPast ? 'past' : ''}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', width: '100%' }}>
            <span style={dayNumberStyle}>{day}</span>
            {isToday && (
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--primary)', boxShadow: '0 0 10px var(--primary)' }}></div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, opacity: isPast ? 0.3 : 1 }}>
            {config.icon === 'past' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            )}
            {config.icon === 'occupied' && (
              <X size={20} strokeWidth={3} color="rgb(239, 68, 68)" />
            )}
            {config.icon === 'available' && (
              <Check size={20} strokeWidth={3} color="rgb(34, 197, 94)" />
            )}
          </div>
        </div>
      );
    }
    return calendarDays;
  };

  // Expertise helpers
  const paintedParts = ALL_PARTS.filter(p => vehicle.expertise_report?.[p.key] === 'painted' || vehicle.expertise_report?.[p.key] === 'local_painted');
  const changedParts = ALL_PARTS.filter(p => vehicle.expertise_report?.[p.key] === 'changed');

  return (
    <div className="bg-bg-main min-h-screen text-white">
      <Navbar />

      <main className="pt-52 pb-12">
        <div className="container mt-20 space-y-12">

          {/* Header Section */}
          <div className="text-left glass p-10 rounded-[2.5rem] border border-white/10" style={{ boxShadow: 'var(--shadow-lg)', marginTop: '20px' }}>
            <h1 className="text-xl lg:text-2xl font-bold uppercase tracking-tight text-white">{vehicle.title}</h1>
          </div>

          {/* TOP GRID: Gallery & Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            {/* Left: Gallery */}
            <div className="lg:col-span-8 flex flex-col h-full">
              <ImageCarousel images={vehicle.images} className="mb-12" />

              <div className="!mt-[20px] glass rounded-[2.5rem] p-12 lg:p-14 text-left shadow-lg border border-white/10 overflow-hidden flex-grow" style={{ boxShadow: 'var(--shadow-lg)', marginTop: '20px' }}>
                <h3 className="text-xl font-black mb-10 border-l-4 border-primary pl-6 uppercase tracking-widest text-primary">İlan Açıklaması</h3>
                <div className="min-h-[100px]">
                  <p className="text-text-muted-light leading-relaxed whitespace-pre-line text-sm lg:text-base">
                    {vehicle.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Sidebar */}
            <div className="lg:col-span-4 h-full">
              <div className="glass rounded-[2.5rem] p-10 lg:p-12 border border-white/10 relative overflow-hidden h-full flex flex-col" style={{ boxShadow: 'var(--shadow-lg)' }}>
                {/* Upper Content Group */}
                <div className="flex-grow">
                  {/* Price */}
                  <div className="mb-10 transition-all">
                    {isRental ? (
                      // Kiralık fiyat kartları
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 900, color: '#64748b', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>Kiralık Fiyatlar</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {vehicle.price_daily && (
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              background: 'rgba(34, 197, 94, 0.08)',
                              border: '1px solid rgba(34, 197, 94, 0.2)',
                              borderRadius: '1rem', padding: '0.9rem 1.2rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <span style={{ fontSize: '1.1rem' }}>☀️</span>
                                <span style={{ fontSize: '0.7rem', fontWeight: 900, color: '#4ade80', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Günlük</span>
                              </div>
                              <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#4ade80' }}>{formatCurrency(vehicle.price_daily)}</span>
                            </div>
                          )}
                          {vehicle.price_weekly && (
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              background: 'rgba(251, 191, 36, 0.08)',
                              border: '1px solid rgba(251, 191, 36, 0.2)',
                              borderRadius: '1rem', padding: '0.9rem 1.2rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <span style={{ fontSize: '1.1rem' }}>📅</span>
                                <span style={{ fontSize: '0.7rem', fontWeight: 900, color: '#fbbf24', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Haftalık</span>
                              </div>
                              <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#fbbf24' }}>{formatCurrency(vehicle.price_weekly)}</span>
                            </div>
                          )}
                          {vehicle.price_monthly && (
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              background: 'rgba(167, 139, 250, 0.08)',
                              border: '1px solid rgba(167, 139, 250, 0.2)',
                              borderRadius: '1rem', padding: '0.9rem 1.2rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <span style={{ fontSize: '1.1rem' }}>🗓️</span>
                                <span style={{ fontSize: '0.7rem', fontWeight: 900, color: '#a78bfa', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Aylık</span>
                              </div>
                              <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#a78bfa' }}>{formatCurrency(vehicle.price_monthly)}</span>
                            </div>
                          )}
                          {!vehicle.price_daily && !vehicle.price_weekly && !vehicle.price_monthly && (
                            <div className="text-3xl font-black text-primary text-left tracking-tight">{formatCurrency(vehicle.price)}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-3xl font-black text-primary text-left tracking-tight">{formatCurrency(vehicle.price)}</div>
                    )}
                  </div>
                  <div className="flex justify-start items-center gap-3 text-text-muted-light border-b border-white/5 pb-8 mb-10">
                    <MapPin size={16} className="text-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest">{vehicle.location.city} / {vehicle.location.district}</span>
                  </div>
                  <ul className="space-y-1">
                    <SpecRow label="Marka" value={vehicle.brand} />
                    <SpecRow label="Yıl" value={vehicle.year} />
                    <SpecRow label="Yakıt" value={vehicle.specs.fuelType} />
                    <SpecRow label="Vites" value={vehicle.specs.transmission} />
                    <SpecRow label="KM" value={vehicle.specs.km} />
                    {!isRental && <SpecRow label="Takas" value={vehicle.specs.exchange} />}
                    {!isRental && <SpecRow label="Tramer" value={vehicle.damage_amount > 0 ? formatCurrency(vehicle.damage_amount) : 'Hasarsız'} />}
                  </ul>
                </div>

                {/* Lower Action Group (Pinned to Bottom) */}
                <div className="mt-12 pt-10 border-t border-white/10 mt-auto">
                  <div className="flex items-center mb-10">
                    <div className="text-left">
                      <h4 className="text-lg font-black uppercase text-white/90 leading-tight">{vehicle.seller?.name}</h4>
                      <p className="text-xs font-bold text-primary mt-1">{vehicle.seller?.phone}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <a href={`tel:${cleanPhone(vehicle.seller?.phone)}`} className="btn btn-primary v2 w-full justify-center py-5 rounded-2xl text-[10px] font-black tracking-[0.1em]">
                      ARA
                    </a>
                    <a href={`https://wa.me/${cleanPhone(vehicle.seller?.phone)}`} className="btn btn-whatsapp w-full justify-center py-5 rounded-2xl text-[10px] font-black tracking-[0.1em] underline decoration-primary underline-offset-4" target="_blank" rel="noopener noreferrer">
                      WHATSAPP
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* BELOW GRID: Parallel Sections */}
          {(() => {
            let sectionNumber = 1;
            return (
              <div className="space-y-12 pb-20">

                {/* Section 1: Vasıta Özellikleri (Her zaman var) */}
                <div className="glass rounded-[2.5rem] p-12 lg:p-16 border border-white/10 w-full" style={{ boxShadow: 'var(--shadow-lg)' }}>
                  <div className="flex items-center gap-5" style={{ marginBottom: '3rem' }}>
                    <div style={{ width: '8px', height: '36px', background: 'linear-gradient(to bottom, var(--primary), #0891b2)', borderRadius: '4px' }}></div>
                    <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white mt-1" style={{ marginLeft: '5px' }}>{sectionNumber++}. Vasıta Özellikleri</h3>
                  </div>
                  <div className="space-y-16" style={{ paddingLeft: '13px' }}>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                      <FeatureList title="Güvenlik" items={vehicle.equipment?.security || []} />
                      <FeatureList title="İç Donanım" items={vehicle.equipment?.interior || []} />
                      <FeatureList title="Dış Donanım" items={vehicle.equipment?.exterior || []} />
                    </div>
                    <div className="py-10 pr-10 bg-primary/5 rounded-3xl border border-primary/10" style={{ marginTop: '2rem' }}>
                      {!isRental ? (
                        // Satılık: Ekspertiz Bilgisi + Kurumsal Satıcı (iki sütun)
                        <div className="grid md:grid-cols-2 gap-10">
                          <div className="text-left">
                            <p className="text-[10px] text-text-muted font-black uppercase mb-3 tracking-widest">Ekspertiz Bilgisi</p>
                            <p className="text-sm font-bold text-white leading-relaxed flex items-center gap-3">
                              <ShieldCheck className="text-primary" /> {vehicle.damageStatus.original}
                            </p>
                          </div>
                          <div className="flex flex-col justify-center border-l border-white/5 pl-10 text-left">
                            <p className="text-[10px] text-text-muted font-black uppercase mb-1 tracking-widest">Kurumsal Satıcı</p>
                            <p className="text-xl font-black text-white">Erenis Otomotiv</p>
                            <p className="text-[10px] text-text-muted font-bold">PLAKA: <span className="text-primary">{vehicle.plate_nationality}</span></p>
                          </div>
                        </div>
                      ) : (
                        // Kiralık: Ekspertiz gizli — Kurumsal Satıcı (Güvenlik hizası) + Plaka (İç Donanım hizası)
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                          <div className="text-left">
                            <p className="text-[10px] text-text-muted font-black uppercase mb-1 tracking-widest">Kurumsal Satıcı</p>
                            <p className="text-xl font-black text-white">Erenis Otomotiv</p>
                          </div>
                          <div className="text-left">
                            <p className="text-[10px] text-text-muted font-black uppercase mb-1 tracking-widest">Plaka</p>
                            <p className="text-sm font-black text-primary">{vehicle.plate_nationality}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {!isRental && vehicle.expertise_report && (
                  <div className="glass rounded-[2.5rem] p-12 lg:p-16 border border-white/10 w-full" style={{ boxShadow: 'var(--shadow-lg)' }}>
                    <div className="flex items-center gap-5 mb-14">
                      <div style={{ width: '8px', height: '36px', background: 'linear-gradient(to bottom, #8b5cf6, #7c3aed)', borderRadius: '4px' }}></div>
                      <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white mt-1" style={{ marginLeft: '5px' }}>{sectionNumber++}. Ekspertiz Raporu</h3>
                    </div>
                    <div className="flex flex-col items-center gap-10">
                      <div className="flex flex-wrap gap-6 justify-center p-4 bg-white/5 rounded-2xl border border-white/5">
                        {[{ label: 'Orijinal', c: '#9ca3af' }, { label: 'Lokal Boyalı', c: '#f97316' }, { label: 'Boyalı', c: '#3b82f6' }, { label: 'Değişen', c: '#ef4444' }].map(s => (
                          <div key={s.label} className="flex items-center gap-2">
                            <div style={{ width: '12px', height: '12px', background: s.c, borderRadius: '3px' }}></div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{s.label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="grid xl:grid-cols-2 gap-16 w-full items-center">
                        <div className="flex justify-center">
                          <div style={{ width: '100%', maxWidth: '240px' }}>
                            <svg viewBox="0 0 500 650" style={{ width: '100%', height: 'auto' }}>
                              <g transform="translate(250, 10)">
                                {[
                                  { key: 'front_bumper', d: "M -50,15 L 50,15 L 50,30 Q 50,35 45,35 L -45,35 Q -50,35 -50,30 Z", lx: 0, ly: 28 },
                                  { key: 'hood', d: "M -55,45 Q 0,35 55,45 L 70,165 Q 0,180 -70,165 Z", lx: 0, ly: 115 },
                                  { key: 'roof', d: "M -85,235 Q 0,250 85,235 L 90,420 Q 0,440 -90,420 Z", lx: 0, ly: 335 },
                                  { key: 'trunk', d: "M -80,480 Q 0,500 80,480 L 70,590 Q 0,610 -70,590 Z", lx: 0, ly: 545 },
                                  { key: 'rear_bumper', d: "M -55,600 L 55,600 L 55,620 Q 55,630 45,630 L -45,630 Q -55,630 -55,620 Z", lx: 0, ly: 618 },
                                ].map(part => {
                                  const s = vehicle.expertise_report?.[part.key] || 'original';
                                  const marker = s === 'changed' ? 'D' : s === 'painted' ? 'B' : s === 'local_painted' ? 'LB' : '';
                                  return (
                                    <g key={part.key}>
                                      <path d={part.d} fill={COLOR_MAP[s]} stroke="rgba(0,0,0,0.1)" strokeWidth="0.5" />
                                      {marker && <text x={part.lx} y={part.ly} fill="white" fontSize="18" fontWeight="900" textAnchor="middle">{marker}</text>}
                                    </g>
                                  );
                                })}
                              </g>
                              <g transform="translate(250, 10)">
                                {[
                                  { key: 'front_left_fender', d: "M -100,55 L -160,70 Q -185,80 -185,120 L -185,170 L -110,170 Z", lx: -145, ly: 125 },
                                  { key: 'front_left_door', d: "M -115,185 L -200,230 L -210,330 L -120,320 Z", lx: -160, ly: 265 },
                                  { key: 'rear_left_door', d: "M -130,340 L -215,360 L -220,480 L -135,480 Z", lx: -175, ly: 415 },
                                  { key: 'rear_left_fender', d: "M -135,495 L -225,495 Q -235,560 -210,590 L -120,610 Z", lx: -175, ly: 555 },
                                  { key: 'front_right_fender', d: "M 100,55 L 160,70 Q 185,80 185,120 L 185,170 L 110,170 Z", lx: 145, ly: 125 },
                                  { key: 'front_right_door', d: "M 115,185 L 200,230 L 210,330 L 120,320 Z", lx: 160, ly: 265 },
                                  { key: 'rear_right_door', d: "M 130,340 L 215,360 L 220,480 L 135,480 Z", lx: 175, ly: 415 },
                                  { key: 'rear_right_fender', d: "M 135,495 L 225,495 Q 235,560 210,590 L 120,610 Z", lx: 175, ly: 555 },
                                ].map(part => {
                                  const s = vehicle.expertise_report?.[part.key] || 'original';
                                  const marker = s === 'changed' ? 'D' : s === 'painted' ? 'B' : s === 'local_painted' ? 'LB' : '';
                                  return (
                                    <g key={part.key}>
                                      <path d={part.d} fill={COLOR_MAP[s]} stroke="rgba(0,0,0,0.1)" strokeWidth="0.5" />
                                      {marker && <text x={part.lx} y={part.ly} fill="white" fontSize="16" fontWeight="900" textAnchor="middle">{marker}</text>}
                                    </g>
                                  );
                                })}
                              </g>
                            </svg>
                          </div>
                        </div>

                        <div className="space-y-10">
                          <div className="grid md:grid-cols-2 gap-10">
                            <div className="flex flex-col">
                              <h4 className="text-blue-400 text-[11px] font-black uppercase tracking-widest mb-6 bg-blue-400/5 w-fit px-4 py-1.5 rounded-lg border border-blue-400/10 text-left">Boyalı Parçalar</h4>
                              <div className="space-y-1">
                                {paintedParts.length > 0 ? paintedParts.map(p => (
                                  <div key={p.key} className="flex items-center gap-3 py-1.5 px-3 rounded-lg hover:bg-white/5 transition-all">
                                    <CheckCircle2 size={15} className="text-blue-400" />
                                    <span className="text-[11px] font-bold text-white/90">{p.label} <span className="text-blue-400/50 ml-1 italic">{vehicle.expertise_report?.[p.key] === 'local_painted' ? '(Lokal)' : ''}</span></span>
                                  </div>
                                )) : <p className="text-[10px] text-gray-500 italic pl-3 text-left">Kayıt bulunamadı.</p>}
                              </div>
                            </div>
                            <div className="flex flex-col">
                              <h4 className="text-red-400 text-[11px] font-black uppercase tracking-widest mb-6 bg-red-500/5 w-fit px-4 py-1.5 rounded-lg border border-red-500/10 text-left">Değişen Parçalar</h4>
                              <div className="space-y-1">
                                {changedParts.length > 0 ? changedParts.map(p => (
                                  <div key={p.key} className="flex items-center gap-3 py-1.5 px-3 rounded-lg hover:bg-white/5 transition-all">
                                    <AlertCircle size={15} className="text-red-400" />
                                    <span className="text-[11px] font-bold text-white/90">{p.label}</span>
                                  </div>
                                )) : <p className="text-[10px] text-gray-500 italic pl-3 text-left">Kayıt bulunamadı.</p>}
                              </div>
                            </div>
                          </div>
                          {vehicle.expertise_note && (
                            <div className="p-6 bg-white/[0.02] rounded-2xl border border-white/5 italic text-[11px] font-bold text-gray-400 text-left">"{vehicle.expertise_note}"</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 3: Takvim (Sadece Kiralık) */}
                {isRental && (
                  <div className="admin-card glass" style={{ marginBottom: '2.5rem', overflow: 'visible', padding: '3rem 4rem', borderRadius: '2.5rem', border: '1px solid rgba(255,255,255,0.1)', boxShadow: 'var(--shadow-lg)' }}>
                    <div className="admin-card-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(0, 209, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                          <CalendarIcon size={22} />
                        </div>
                        <div>
                          <h2 className="admin-card-title text-2xl lg:text-3xl font-black uppercase tracking-tight text-white mt-1" style={{ marginBottom: '0.1rem' }}>{sectionNumber++}. Araç Müsaitlik & Rezervasyon Takvimi</h2>
                          <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Filo planlamasını buradan yapabilirsiniz.</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255, 255, 255, 0.03)', padding: '0.5rem 1rem', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <button onClick={handlePrevMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex' }}>
                            <ChevronLeft size={20} />
                          </button>
                          <span style={{ fontWeight: 800, fontSize: '0.95rem', minWidth: '130px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            {currentDate.toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
                          </span>
                          <button onClick={handleNextMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex' }}>
                            <ChevronRight size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0px', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(d => (
                        <div key={d} style={{ padding: '0.75rem', background: 'rgba(255, 255, 255, 0.03)', textAlign: 'center', fontSize: '0.75rem', fontWeight: 900, color: 'rgb(148, 163, 184)', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', textTransform: 'uppercase' }}>{d}</div>
                      ))}
                      {renderCalendar()}
                    </div>
                    <div style={{ display: 'flex', gap: '2.5rem', margin: '2.5rem 1rem 0 1rem', padding: '1.25rem 1.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '1.25rem', border: '1px solid rgba(255,255,255,0.05)', width: 'fit-content' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-dim)', letterSpacing: '1px' }}>
                        <Check size={16} strokeWidth={3} color="rgb(34, 197, 94)" style={{ opacity: 0.8 }} />
                        <span className="uppercase">Araç Müsait</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-dim)', letterSpacing: '1px' }}>
                        <X size={16} strokeWidth={3} color="rgb(239, 68, 68)" style={{ opacity: 0.8 }} />
                        <span className="uppercase">Araç Müsait Değil</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 4: Donanım Özellikleri (Her zaman var) */}
                <div className="glass rounded-[2.5rem] p-12 lg:p-16 border border-white/10 w-full !mb-[40px]" style={{ boxShadow: 'var(--shadow-lg)', marginBottom: '40px' }}>
                  <div className="flex items-center gap-5" style={{ marginBottom: '3rem' }}>
                    <div style={{ width: '8px', height: '36px', background: 'linear-gradient(to bottom, var(--primary), #0891b2)', borderRadius: '4px' }}></div>
                    <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white mt-1" style={{ marginLeft: '5px' }}>{sectionNumber++}. Donanım Özellikleri</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-12">
                    {Object.entries(FEATURE_GROUPS).map(([groupName, items]) => {
                      const activeItemsInGroup = items.filter(f => vehicle.dbFeatures.includes(f));
                      if (activeItemsInGroup.length === 0) return null;
                      return (
                        <div key={groupName} className="flex flex-col">
                          <h4 className="text-primary text-[11px] font-black uppercase tracking-[0.2em] bg-primary/5 w-fit px-5 py-2 rounded-xl border border-primary/10 mt-1" style={{ marginBottom: '1.5rem' }}>{groupName}</h4>
                          <div className="space-y-1">
                            {items.map(feature => {
                              const isActive = vehicle.dbFeatures.includes(feature);
                              return (
                                <div key={feature} className="flex items-center gap-3 py-2 px-3 rounded-xl transition-all hover:bg-white/5 group/feat" style={{ opacity: isActive ? 1 : 0.25 }}>
                                  {isActive ? <CheckCircle2 size={16} className="text-primary flex-shrink-0" /> : <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }}></div>}
                                  <span className={`text-[11px] font-bold tracking-tight leading-tight ${isActive ? 'text-white/90' : 'text-gray-500'}`}>{feature}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            );
          })()}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const SpecRow = ({ label, value }) => (
  <li className="flex items-center justify-between py-5 border-b border-white/5 transition-all hover:bg-white/5 px-2 rounded-xl">
    <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">{label}</span>
    <span className="text-[11px] font-black text-white tracking-tight">{value}</span>
  </li>
);

const FeatureList = ({ title, items }) => (
  <div className="text-left">
    <h4 className="text-primary text-[9px] font-black uppercase tracking-[0.2em] bg-primary/5 w-fit px-5 py-2 rounded-lg" style={{ marginBottom: '1.5rem' }}>{title}</h4>
    <ul className="space-y-4">
      {items.length > 0 ? items.map(f => (
        <li key={f} className="flex items-center gap-4 text-[11px] font-bold text-gray-400">
          <CheckCircle2 size={14} className="text-primary" /> {f}
        </li>
      )) : (
        <li className="text-[11px] font-bold text-gray-600 italic">Belirtilmemiş</li>
      )}
    </ul>
  </div>
);

export default VehicleDetail;